'use client';

import { type DegenOptions, Liveline } from '@morfi/chart';
import { useReducedMotion } from 'motion/react';
import { useTheme } from 'next-themes';
import { useEffect, useRef, useState } from 'react';

import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { outcomeHueToStepVar } from '@/lib/chart/outcome-hue';
import { cn } from '@/lib/ui/utils';

import type { OutcomeSparklineColorClasses, OutcomeSparklinePoint } from './outcome-sparkline';

export interface OutcomeLivelineProps {
  /**
   * TWAR series for this outcome, oldest → newest (the slot owner's resting
   * 6h window, or full market history while the card is hovered). The slot owner
   * (`<OutcomeSparkline>`) guarantees `data.length >= 2` before mounting this
   * wrapper; the flat-line fallback stays upstream.
   */
  readonly data: ReadonlyArray<OutcomeSparklinePoint>;
  /** Enables upstream Degen effects after the first verified realtime trade. */
  readonly degenTriggerId?: string | null;
  /** Exact query viewport committed with `data`; legacy callers may derive it. */
  readonly timeWindowSecs?: number;
  /** Reports completion of the canvas-owned viewport transition. */
  readonly onWindowTransitionComplete?: (windowSecs: number) => void;
  /**
   * Outcome color slot from `useOutcomeColor()`. Only `hue` is read here;
   * the others are kept on the type for parity with the slot owner.
   */
  readonly colorClasses: OutcomeSparklineColorClasses;
  readonly className?: string;
}

// LiveChart's default endpoint pulse expands to ~21 px. Pad the canvas on the
// three sides where the pulse can reach (top/right/bottom) so the ring is
// not clipped at the chart-well edge. Left padding stays minimal — the line
// starts there and has no endpoint.
const ENDPOINT_PAD: number = Math.ceil(21);

// LiveChart paints into a canvas, which cannot resolve `var(--…)`. Resolve the
// explicit line colour here so it remains the chart's highest-priority colour
// without requiring the chart package to change its option precedence.
// Mirrors the pattern in `winners-row-liveline.tsx` and `market-chart.tsx`;
// kept local — lifting to a shared util buys nothing for three callsites.
function resolveCssVar(input: string, fallback: string = input): string {
  if (typeof document === 'undefined') return fallback;
  const match = /^var\((--[^,)]+)(?:,\s*([^)]+))?\)$/.exec(input);
  if (!match) return input;
  const varName = match[1] ?? '';
  const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  if (resolved.length > 0) return resolved;
  const inlineFallback = match[2];
  return inlineFallback !== undefined ? inlineFallback.trim() : fallback;
}

/**
 * Featured-market-hero outcome chart — canvas-based live preview that
 * replaces the static SVG sparkline inside `<OutcomeSparkline>`'s filled-
 * data branch. Wraps `<LiveChart>` from `@morfi/chart` with the instrument
 * chrome off (no axes, no labels, no crosshair, no badge) and `momentum`
 * coloring off (the endpoint dot stays the outcome accent, never flips
 * green/red), but `degen` ON: a verified post-mount trade can fire a particle
 * burst + brief shake when its outcome line moved enough.
 *
 * Mirrors the structural pattern in `winners-row-liveline.tsx` (intersection-
 * observer mount gate, sticky-mount latch, `resolveCssVar` helper) but is
 * tuned for the hero's larger `h-16` chart well: endpoint dot/pulse render
 * at the package's default size, and `nowOverride` is live-anchored — ticks
 * every minute so the endpoint dot drifts rightward in real time over the
 * user's session.
 *
 * Slot-owner contract: the parent `<OutcomeSparkline>` shows only the underlay
 * while loading and a flat line for settled data with fewer than two points.
 * This wrapper assumes at least two points.
 */
export function OutcomeLiveline({
  data,
  degenTriggerId,
  timeWindowSecs,
  onWindowTransitionComplete,
  colorClasses,
  className,
}: OutcomeLivelineProps): React.ReactNode {
  const { resolvedTheme } = useTheme();
  const theme: 'light' | 'dark' = resolvedTheme === 'light' ? 'light' : 'dark';
  const reduceMotion = useReducedMotion() === true;

  // Live "now" anchor. Captured ONCE via lazy initializer (avoids RC-004
  // `Date.now()` in render). Advanced by `setInterval` once mounted so the
  // endpoint dot drifts rightward inside the fixed window. Localised to
  // this wrapper so parent components (panel, grid, card) do not re-render
  // on the tick — see spec SC-007.
  const [liveNowSec, setLiveNowSec] = useState<number>(() => Math.floor(Date.now() / 1000));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isIntersecting } = useIntersectionObserver(containerRef, { rootMargin: '200px' });
  const [mounted, setMounted] = useState<boolean>(false);
  useEffect(() => {
    if (isIntersecting && !mounted) setMounted(true);
  }, [isIntersecting, mounted]);

  // Per-minute tick — only runs while mounted. Cleanup clears the interval
  // so Strict-Mode double-invoke is safe.
  useEffect(() => {
    if (!mounted) return;
    const id = setInterval(() => {
      setLiveNowSec(Math.floor(Date.now() / 1000));
    }, 60_000);
    return (): void => {
      clearInterval(id);
    };
  }, [mounted]);

  const color = resolveCssVar(outcomeHueToStepVar(colorClasses.hue, 400));
  const points = data.map((p) => ({ time: p.time, value: p.value }));
  const lastValue = points[points.length - 1]?.value ?? 0;
  // Window = (first bucket → now). Derive it from the actual bucket timestamps,
  // NOT a fixed bucket size × point count: the backend's TWAR series is
  // span-adaptive (5m / 15m / 1h … buckets depending on the range), so assuming
  // a constant 1h bucket over-sized the window several-fold and crushed the line
  // into the right edge. With `nowOverride = liveNowSec`, the engine fills the
  // canvas left→right and draws a flat tip from the last bucket to now.
  const firstTime = points[0]?.time;
  const windowSecs =
    timeWindowSecs ?? (firstTime !== undefined ? Math.max(60, liveNowSec - firstTime) : 0);

  // Reduced-motion ⇒ collapse value-smoothing to a single frame; pulse stays
  // on by deliberate design choice (mirrors winners convention — the pulse
  // is the "this is live" signal, not vestibular motion).
  // `exactOptionalPropertyTypes`: spread to omit `smoothing` entirely when
  // motion is allowed, instead of passing `undefined`.
  const smoothingProp: { lerpSpeed?: number } = reduceMotion ? { lerpSpeed: 1 } : {};

  // Degen mode — particle bursts + chart shake on verified post-mount trades,
  // both directions (`downMomentum: true`). Passing null is deliberate: the
  // chart engine treats an omitted trigger as legacy swing-driven mode, which
  // lets historical data or timeframe transitions fire particles. Suppressed
  // under reduced motion because bursts/shake are vestibular motion.
  const degen: false | DegenOptions =
    reduceMotion || degenTriggerId === undefined || degenTriggerId === null
      ? false
      : { downMomentum: true };
  return (
    <div
      ref={containerRef}
      className={cn('pointer-events-none block h-full w-full', className)}
      aria-hidden
      role="presentation"
    >
      {mounted ? (
        <Liveline
          data={points}
          value={lastValue}
          color={color}
          lineWidth={1.5}
          theme={theme}
          window={windowSecs}
          {...(onWindowTransitionComplete === undefined ? {} : { onWindowTransitionComplete })}
          grid={false}
          timeAxis={false}
          scrub={false}
          valueLine={false}
          edgeFade={false}
          pulse
          badge={false}
          momentum={false}
          degen={degen}
          fill
          {...smoothingProp}
          padding={{ top: ENDPOINT_PAD, right: ENDPOINT_PAD, bottom: ENDPOINT_PAD, left: 4 }}
          className="block h-full w-full"
        />
      ) : null}
    </div>
  );
}
